/**
 * Milestone Date Field Mapper
 *
 * Maps milestone dates to the correct backend field names based on status.
 * The backend expects different date field names depending on the milestone status:
 * - Approved statuses → approvedDate, approvedOn, originalApprovedOn
 * - Not Approved statuses → notApprovedDate, notApprovedOn
 * - Sent statuses → sentDate, sentOn
 * - Scheduled statuses → meetingScheduledOn, scheduledDate
 * - Target statuses → targetDate
 * - Complete statuses → completedDate, completedOn
 *
 * This ensures the frontend sends data in the format the backend expects.
 */

/**
 * Date field mapping configuration for each status type
 */
interface DateFieldMapping {
  primaryDateField: string; // The primary date field name for this status
  secondaryDateField?: string; // Optional secondary date field
  approverField?: string; // The field name for the approver
  timeField?: string; // Optional time field
}

const STATUS_DATE_FIELD_MAP: Record<string, DateFieldMapping> = {
  // TARGET statuses
  target: {
    primaryDateField: 'targetDate'
  },
  async_target: {
    primaryDateField: 'targetDate'
  },

  // SCHEDULED statuses
  meeting_scheduled: {
    primaryDateField: 'meetingScheduledOn',
    timeField: 'meetingScheduledAt'
  },
  scheduled: {
    primaryDateField: 'scheduledDate',
    timeField: 'scheduledAt'
  },

  // APPROVED statuses (Meeting)
  approved: {
    primaryDateField: 'approvedOn',
    approverField: 'approvedBy',
    timeField: 'approvedTime'
  },

  // APPROVED statuses (Async)
  approved_async: {
    primaryDateField: 'approvedOn',
    approverField: 'approvedBy'
  },

  // RE-APPROVED statuses
  re_approved: {
    primaryDateField: 'originalApprovedOn',
    secondaryDateField: 'reApprovedOn',
    approverField: 'reApprovedBy'
  },
  re_approved_async: {
    primaryDateField: 'originalApprovedOn',
    secondaryDateField: 'reApprovedOn',
    approverField: 'reApprovedBy'
  },

  // NOT APPROVED statuses (Meeting)
  not_approved: {
    primaryDateField: 'notApprovedOn',
    approverField: 'notApprovedBy',
    timeField: 'notApprovedTime'
  },

  // NOT APPROVED statuses (Async)
  not_approved_async: {
    primaryDateField: 'notApprovedOn',
    approverField: 'notApprovedBy'
  },

  // NOT APPROVED - To be Approved Async
  not_approved_to_be_approved_async: {
    primaryDateField: 'notApprovedOn',
    secondaryDateField: 'approvalDueDate',
    approverField: 'notApprovedBy'
  },

  // APPROVED - Follow Up
  approved_follow_up: {
    primaryDateField: 'approvedOn',
    secondaryDateField: 'followUpDate',
    approverField: 'approvedBy'
  },

  // SENT statuses (Async only)
  sent: {
    primaryDateField: 'sentOn',
    secondaryDateField: 'dueDate'
  },
  sent_pending: {
    primaryDateField: 'sentOn',
    secondaryDateField: 'pendingUntil'
  },

  // COMPLETE statuses
  complete: {
    primaryDateField: 'completedOn',
    approverField: 'completedBy',
    timeField: 'completedTime'
  },
  complete_original_date: {
    primaryDateField: 'completedOn',
    secondaryDateField: 'originalDate',
    approverField: 'completedBy'
  },

  // REPLANNING statuses
  replanning_approval_required: {
    primaryDateField: 'replanningDate'
  },
  replanning_no_approval: {
    primaryDateField: 'replanningDate'
  },

  // PENDING statuses
  meeting_pending: {
    primaryDateField: 'meetingScheduledOn',
    secondaryDateField: 'pendingUntil',
    timeField: 'meetingScheduledAt'
  }
};

/**
 * Maps milestone form data to backend-expected field names based on status
 * @param formData - The form data from EditMilestoneModal
 * @returns Object with correctly named date fields for the backend
 */
export function mapMilestoneDateFields(formData: any): any {
  const status = formData.status?.toLowerCase();

  if (!status) {
    // No status, return generic date field
    return {
      date: formData.date,
      secondaryDate: formData.secondaryDate
    };
  }

  const mapping = STATUS_DATE_FIELD_MAP[status];

  if (!mapping) {
    // Status not in mapping, use generic fields as fallback
    console.warn(
      `No date field mapping found for status: ${status}, using generic 'date' field`
    );
    return {
      date: formData.date,
      secondaryDate: formData.secondaryDate
    };
  }

  const result: any = {};

  // Map primary date field
  if (formData.date) {
    result[mapping.primaryDateField] = formData.date;
    // Also keep 'date' for backward compatibility
    result.date = formData.date;
  }

  // Map secondary date field
  if (formData.secondaryDate && mapping.secondaryDateField) {
    result[mapping.secondaryDateField] = formData.secondaryDate;
    // Also keep 'secondaryDate' for backward compatibility
    result.secondaryDate = formData.secondaryDate;
  }

  // Map approver field
  if (formData.approver && mapping.approverField) {
    result[mapping.approverField] = formData.approver;
    // Also keep 'approver' for backward compatibility
    result.approver = formData.approver;
  }

  // Map time field (for meeting scheduled)
  if (formData.meetingScheduledAt && mapping.timeField) {
    result[mapping.timeField] = formData.meetingScheduledAt;
  }

  return result;
}

/**
 * Gets the appropriate date field name for a given status
 * Useful for knowing which field to read from backend responses
 * @param status - The milestone status
 * @param fieldType - The type of field ('primary', 'secondary', 'approver', 'time')
 * @returns The backend field name
 */
export function getDateFieldName(
  status: string,
  fieldType: 'primary' | 'secondary' | 'approver' | 'time' = 'primary'
): string {
  const statusKey = status?.toLowerCase();
  const mapping = STATUS_DATE_FIELD_MAP[statusKey];

  if (!mapping) {
    // Fallback to generic names
    switch (fieldType) {
      case 'primary':
        return 'date';
      case 'secondary':
        return 'secondaryDate';
      case 'approver':
        return 'approver';
      case 'time':
        return 'meetingScheduledAt';
      default:
        return 'date';
    }
  }

  switch (fieldType) {
    case 'primary':
      return mapping.primaryDateField;
    case 'secondary':
      return mapping.secondaryDateField || 'secondaryDate';
    case 'approver':
      return mapping.approverField || 'approver';
    case 'time':
      return mapping.timeField || 'meetingScheduledAt';
    default:
      return mapping.primaryDateField;
  }
}

/**
 * Reverse mapping: Given backend milestone data, normalize it to frontend format
 * This helps when reading milestone data from the backend
 * @param milestoneData - The milestone data from backend
 * @returns Normalized milestone data with 'date', 'secondaryDate', 'approver' fields
 */
export function normalizeMilestoneData(milestoneData: any): any {
  if (!milestoneData || !milestoneData.status) {
    return milestoneData;
  }

  const status = milestoneData.status.toLowerCase();
  const mapping = STATUS_DATE_FIELD_MAP[status];

  if (!mapping) {
    return milestoneData;
  }

  const normalized = { ...milestoneData };

  // Normalize primary date to 'date'
  if (milestoneData[mapping.primaryDateField] && !normalized.date) {
    normalized.date = milestoneData[mapping.primaryDateField];
  }

  // Normalize secondary date to 'secondaryDate'
  if (
    mapping.secondaryDateField &&
    milestoneData[mapping.secondaryDateField] &&
    !normalized.secondaryDate
  ) {
    normalized.secondaryDate = milestoneData[mapping.secondaryDateField];
  }

  // Normalize approver to 'approver'
  if (
    mapping.approverField &&
    milestoneData[mapping.approverField] &&
    !normalized.approver
  ) {
    normalized.approver = milestoneData[mapping.approverField];
  }

  // Normalize time to 'meetingScheduledAt'
  if (
    mapping.timeField &&
    milestoneData[mapping.timeField] &&
    !normalized.meetingScheduledAt
  ) {
    normalized.meetingScheduledAt = milestoneData[mapping.timeField];
  }

  return normalized;
}

/**
 * Get all field mappings for a specific status
 * Useful for debugging and understanding what fields are expected
 * @param status - The milestone status
 * @returns The complete mapping configuration
 */
export function getStatusMapping(status: string): DateFieldMapping | null {
  const statusKey = status?.toLowerCase();
  return STATUS_DATE_FIELD_MAP[statusKey] || null;
}

/**
 * Check if a status requires a secondary date field
 * @param status - The milestone status
 * @returns True if the status requires/supports a secondary date
 */
export function statusHasSecondaryDate(status: string): boolean {
  const statusKey = status?.toLowerCase();
  const mapping = STATUS_DATE_FIELD_MAP[statusKey];
  return !!(mapping && mapping.secondaryDateField);
}

/**
 * Check if a status requires an approver field
 * @param status - The milestone status
 * @returns True if the status requires an approver
 */
export function statusRequiresApprover(status: string): boolean {
  const statusKey = status?.toLowerCase();
  const mapping = STATUS_DATE_FIELD_MAP[statusKey];
  return !!(mapping && mapping.approverField);
}

/**
 * Check if a status requires a time field
 * @param status - The milestone status
 * @returns True if the status requires time (for scheduled meetings)
 */
export function statusRequiresTime(status: string): boolean {
  const statusKey = status?.toLowerCase();
  const mapping = STATUS_DATE_FIELD_MAP[statusKey];
  return !!(mapping && mapping.timeField);
}
